import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import { Check, ArrowLeft, Sparkles, ArrowRight } from "lucide-react";

export default function PricingPage({ standalone = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])); }, []);

  const upgrade = async (planKey) => {
    if (!user) { navigate(`/register?plan=${planKey}`); return; }
    setBusy(planKey);
    try {
      const { data } = await api.post("/billing/checkout", { plan: planKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout");
    } finally { setBusy(null); }
  };

  const content = (
    <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
      <div className="mb-14 max-w-3xl">
        <div className="label-mono text-primary">// pricing</div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter sm:text-5xl">
          Fair pricing. <span className="gradient-cyan-lime">Serious learning.</span>
        </h1>
        <p className="mt-3 text-muted-foreground">Cancel anytime. All plans include your personal mentor.</p>
      </div>

      <div className="grid gap-6 md:grid-cols-4">
        {plans.map((p) => {
          const highlight = p.key === "pro_monthly";
          const displayPrice = p.price_cents === 0 ? "$0" : `$${(p.price_cents/100).toFixed(0)}`;
          const currentPlan = (user?.plan || "free") === (p.key.startsWith("pro") ? "pro" : p.key.startsWith("business") ? "business" : "free");
          return (
            <div
              key={p.key}
              className={`relative rounded-2xl border p-6 ${highlight ? "border-primary/50 bg-primary/5 shadow-[0_20px_60px_-20px_rgba(0,229,255,0.35)]" : "border-border bg-card"}`}
              data-testid={`pricing-card-${p.key}`}
            >
              {highlight && (
                <div className="absolute -top-3 left-4 rounded-full bg-primary px-3 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-primary-foreground">
                  Most popular
                </div>
              )}
              <div className="label-mono">{p.name}</div>
              <div className="mt-2 flex items-end gap-1">
                <div className="font-display text-4xl font-black tracking-tighter">{displayPrice}</div>
                {p.interval && <div className="pb-1 label-mono">/{p.interval === "year" ? "yr" : "mo"}</div>}
              </div>
              <ul className="mt-5 space-y-2">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 text-primary" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`pricing-cta-${p.key}`}
                onClick={() => p.key === "free" ? navigate("/register") : upgrade(p.key)}
                disabled={busy === p.key || currentPlan}
                className={`mt-6 w-full rounded-full font-mono text-xs uppercase tracking-widest ${highlight ? "btn-neon" : ""}`}
                variant={highlight ? "default" : "outline"}
              >
                {currentPlan ? "Current plan" : busy === p.key ? "Redirecting…" : p.key === "free" ? "Get started" : "Upgrade"}
                {!currentPlan && p.key !== "free" && <ArrowRight className="ml-2 h-3.5 w-3.5" />}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-14 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
        <div className="label-mono text-primary mb-2">// beta note</div>
        We're in closed beta. Stripe test cards work today · use <span className="font-mono">4242 4242 4242 4242</span> · any future expiry · any CVC.
      </div>
    </div>
  );

  if (!standalone) return content;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-lg font-black">LaneX Academy</span>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button size="sm" onClick={() => navigate("/app/dashboard")} className="rounded-full font-mono text-xs uppercase tracking-widest">Dashboard</Button>
            ) : (
              <Button size="sm" onClick={() => navigate("/login")} className="rounded-full font-mono text-xs uppercase tracking-widest">Sign in</Button>
            )}
          </div>
        </div>
      </header>
      {content}
    </div>
  );
}
